import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Col, Container, Row, Button, Spinner } from 'reactstrap';
import CommonTableIndexWise from '../../../../Components/Common/CommonTableIndexWise';
import { useGlobalStudy } from 'context/StudyContext';
import { useReportsSummaryService } from 'modules/Pharmacy/Reports/api/useReportsSummary';
import { reportsSummaryApiService } from 'modules/Pharmacy/Reports/api/reportsSummary.api';
import CommonSearch from 'Components/Common/CommonSearch';
import StatCard from '../../../../Components/Common/StatCard';
import XLSX from 'xlsx-js-style';
import { formatDate, GetUserData } from 'common/service/CommonService';
import SubjectWiseFilterModal, { SubjectWiseFilterValues } from '../components/SubjectWiseFilterModal';
import sf from '../../../../assets/scss/common/searchFilter.module.scss';
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from 'common/constants/PermissionConstants';
import { BreadcrumbContext } from 'Layouts/Header';
import { REPORTS_PATHS } from 'Routes/paths';

const SubjectWiseReport = () => {
    const breadcrumbContext = useContext(BreadcrumbContext);

    useEffect(() => {
        breadcrumbContext?.setBreadcrumbItems?.([
            { text: "Pharmacy Reports", link: REPORTS_PATHS.SUMMARY },
            { text: "Subject Wise Report" }
        ]);
        return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }, []);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [searchText, setSearchText] = useState<string>("");
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState<SubjectWiseFilterValues>({});
    const [isFilterApplied, setIsFilterApplied] = useState(false);

    const { selectedStudy: globalStudy } = useGlobalStudy();
    const [selectedStudy, setSelectedStudy] = useState<string>("");

    useEffect(() => {
        if (globalStudy?.studyid) {
            setSelectedStudy(String(globalStudy.studyid));
        }
        else {
            setSelectedStudy("");
        }
    }, [globalStudy]);

    const { SelectSubjectWiseQuery } = useReportsSummaryService();
    const activeStudyId = selectedStudy || globalStudy?.studyid || null;

    const toggleFilter = () => setIsFilterOpen(p => !p);

    const applyFilters = (values: SubjectWiseFilterValues) => {
        const isAllEmpty = Object.values(values).every(
            (value) => value === null || value === "" || value === undefined
        );

        if (isAllEmpty) {
            setFilters({});
            setIsFilterApplied(false);
        } else {
            setFilters(values);
            setIsFilterApplied(true);
            setSearchText("");
        }
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({});
        setIsFilterApplied(false);
        setCurrentPage(1);
    };

    // API binding using React Query
    const subjectWiseQuery = SelectSubjectWiseQuery({
        studyid: activeStudyId ? Number(activeStudyId) : null,
        searchtext: searchText,
        pagesize: itemsPerPage,
        currentpage: currentPage,
        subjectno: filters?.subjectno,
        ipcategory: filters?.ipcategory,
        actionname: filters?.actionname,
        fromdate: filters?.fromDate,
        todate: filters?.toDate,
        pagination: true,
    });
    const apiData = subjectWiseQuery.data?.data || [];

    // Dynamic stats computation from response payload
    const stats = useMemo(() => {
        if (!apiData || apiData.length === 0) {
            return {
                totalSubjects: 0,
                totalDispensed: 0,
                totalReturn: 0,
                netRemaining: 0
            };
        }
        const firstItem = apiData[0];
        return {
            totalSubjects: firstItem.totalsubjects || 0,
            totalDispensed: firstItem.totaldispensed || 0,
            totalReturn: firstItem.totalreturn || 0,
            netRemaining: firstItem.netremaining || 0
        };
    }, [apiData]);

    const columns: any = [
        {
            key: 'subjectno',
            label: 'Subject No',
            render: (val: any) => <span className="text-dark fw-bold fs-13">{val || '-'}</span>
        },
        {
            key: 'studycode',
            label: 'Study Code',
            render: (val: any) => <span className="text-muted fw-semibold fs-13">{val || '-'}</span>
        },
        {
            key: 'ipcategory',
            label: 'IP Category',
            render: (val: any) => {
                const isTestProduct =
                    val?.toLowerCase() === 'test product';

                return (
                    <span
                        className="px-2 py-1 fs-11 rounded fw-semibold"
                        style={{
                            background: isTestProduct
                                ? 'rgba(124, 58, 237, 0.10)'
                                : 'rgba(15, 118, 110, 0.10)',

                            color: isTestProduct
                                ? '#7c3aed'
                                : '#0f766e',

                            border: isTestProduct
                                ? '1px solid rgba(124, 58, 237, 0.18)'
                                : '1px solid rgba(15, 118, 110, 0.18)',

                            letterSpacing: '0.2px'
                        }}
                    >
                        {val || '-'}
                    </span>
                );
            }
        },
        {
            key: 'productname',
            label: 'Product Name',
            render: (val: any) => <span className="text-dark fw-bold fs-13">{val || '-'}</span>
        },
        {
            key: 'batchlotno',
            label: 'Batch Lot No',
            render: (val: any) => <span className="badge bg-light text-muted border px-2 py-1 fs-12">{val || '-'}</span>
        },
        {
            key: 'actionname',
            label: 'Action Name',
            render: (val: any) => {
                let badgeColor = 'info';
                if (val?.toLowerCase() === 'dispense') badgeColor = 'success';
                if (val?.toLowerCase() === 'return') badgeColor = 'danger';
                return (
                    <span className={`badge bg-${badgeColor}-subtle text-${badgeColor} border border-${badgeColor}-subtle px-2 py-1 fs-11 rounded fw-bold`}>
                        {val || '-'}
                    </span>
                );
            }
        },
        {
            key: 'actiondate',
            label: 'Action Date',
            render: (val: any) => <span className="text-muted fw-semibold fs-13">{formatDate(val) || '-'}</span>
        },
        {
            key: 'opening',
            label: 'Opening',
            render: (val: any) => <span className="text-muted fs-13 fw-semibold">{val ?? 0}</span>
        },
        {
            key: 'credit',
            label: 'Credit',
            render: (val: any) => (
                <span className="text-success fw-bold fs-13">
                    {val > 0 ? `+${val}` : val ?? 0}
                </span>
            )
        },
        {
            key: 'debit',
            label: 'Debit',
            render: (val: any) => (
                <span className="text-danger fw-bold fs-13">
                    {val > 0 ? `-${val}` : val ?? 0}
                </span>
            )
        },
        {
            key: 'closing',
            label: 'Closing',
            render: (val: any) => <span className="text-dark fw-semibold fs-13">{val ?? 0}</span>
        },
    ];

    const onSearch = (text: string | null) => {
        setSearchText(text ?? "");
        setCurrentPage(1);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const res = await reportsSummaryApiService.getSubjectWiseReport({
                studyid: activeStudyId ? Number(activeStudyId) : null,
                searchtext: searchText,
                subjectno: filters?.subjectno,
                ipcategory: filters?.ipcategory,
                actionname: filters?.actionname,
                fromdate: filters?.fromDate,
                todate: filters?.toDate,
                pagination: false,
            });

            const exportData = res?.data || [];
            const fileName = `Subject_Wise_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

            const headerRow = [
                "Subject No",
                "Study Code",
                "IP Category",
                "Product Name",
                "Batch / Lot No",
                "Action Name",
                "Action Date",
                "Opening",
                "Credit",
                "Debit",
                "Closing"
            ];
            const user = GetUserData();

            const dataRows = exportData.map((item: any) => [
                item.subjectno || "-",
                item.studycode || "-",
                item.ipcategory || "-",
                item.productname || "-",
                item.batchlotno || "-",
                item.actionname || "-",
                formatDate(item.actiondate) || "-",
                Number(item.opening) || 0,
                Number(item.credit) || 0,
                Number(item.debit) || 0,
                Number(item.closing) || 0
            ]);

            const titleRow = ["SUBJECT WISE IP REPORT", "", "", "", "", "", "", "", "", "📋", ""];
            const subtitle = ["Detailed report of IP movements, dispensing, and returns tracked per subject", "", "", "", "", "", "", "", "", "", ""];
            const generatedRow = [`Generated On: ${new Date().toLocaleString()} | Generated By: ${user?.userdisplayname || 'System User'}`, "", "", "", "", "", "", "", "", "", ""];

            const allRows = [
                [""], // Row 1: Spacer
                titleRow, // Row 2
                subtitle, // Row 3
                generatedRow, // Row 4
                [""], // Row 5: Spacer between generated metadata and table headers (new!)
                headerRow, // Row 6
                ...dataRows,
                [""], // Spacer Row before warning note
                [`🛡️ Note: Figures are system generated. Please verify before taking action.`, "", "", "", "", "", "", "", "", "", ""] // Footer warning row
            ];

            const ws = XLSX.utils.aoa_to_sheet(allRows);

            // Core Premium Palette Styles (Segoe UI Font Family)
            const titleStyle = {
                font: { name: "Segoe UI", sz: 18, bold: true, color: { rgb: "064E3B" } }, // Dark green
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "FFFFFF" } }
            };

            const subtitleStyle = {
                font: { name: "Segoe UI", sz: 10, italic: true, color: { rgb: "047857" } }, // Medium green
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "FFFFFF" } }
            };

            const metaStyle = {
                font: { name: "Segoe UI", sz: 9.5, color: { rgb: "4B5563" } }, // Cool gray
                alignment: { vertical: "center", horizontal: "center" }, // Center aligned!
                fill: { fgColor: { rgb: "FFFFFF" } }
            };

            const iconStyle = {
                font: { name: "Segoe UI Emoji", sz: 32 }, // Segoe UI Emoji renders the clipboard 📋 beautifully
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "EBF7EE" } } // Premium mint-green card background
            };

            const headerStyle = {
                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "064E3B" } }, // Deep forest green header
                alignment: { vertical: "center", horizontal: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "043E2B" } },
                    bottom: { style: "thin", color: { rgb: "043E2B" } },
                    left: { style: "thin", color: { rgb: "043E2B" } },
                    right: { style: "thin", color: { rgb: "043E2B" } }
                }
            };

            const cellBorder = {
                top: { style: "thin", color: { rgb: "E2E8F0" } },
                bottom: { style: "thin", color: { rgb: "E2E8F0" } },
                left: { style: "thin", color: { rgb: "E2E8F0" } },
                right: { style: "thin", color: { rgb: "E2E8F0" } }
            };

            const textStyle = {
                font: { name: "Segoe UI", sz: 10, color: { rgb: "1F2937" } },
                alignment: { vertical: "center", horizontal: "center" },
                border: cellBorder
            };

            const footerStyle = {
                font: { name: "Segoe UI", sz: 10, italic: true, bold: true, color: { rgb: "064E3B" } }, // Dark green
                alignment: { vertical: "center", horizontal: "center" },
                fill: { fgColor: { rgb: "EBF7EE" } } // Super light mint green
            };

            const getIpCategoryStyle = (val: string, rowFill: any) => {
                const isTestProduct = val?.toLowerCase() === 'test product';
                return {
                    font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: isTestProduct ? "7C3AED" : "0F766E" } },
                    fill: rowFill,
                    alignment: { vertical: "center", horizontal: "center" },
                    border: cellBorder
                };
            };

            // Custom Merge Rules
            ws["!merges"] = [
                // Title (A2:I2)
                { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },
                // Subtitle (A3:I3)
                { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } },
                // Meta info (A4:I4)
                { s: { r: 3, c: 0 }, e: { r: 3, c: 8 } },
                // Icon (J2:K4)
                { s: { r: 1, c: 9 }, e: { r: 3, c: 10 } },
                // Warning note at the end (A_last:K_last)
                { s: { r: 6 + exportData.length + 1, c: 0 }, e: { r: 6 + exportData.length + 1, c: 10 } }
            ];

            const getIconBorder = (r: number, c: number) => {
                return {
                    top: r === 1 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                    bottom: r === 3 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                    left: c === 9 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                    right: c === 10 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined
                };
            };

            const getFooterBorder = (c: number) => {
                const border: any = {
                    top: { style: "thin", color: { rgb: "A5D6A7" } },
                    bottom: { style: "thin", color: { rgb: "A5D6A7" } }
                };
                if (c === 0) border.left = { style: "thin", color: { rgb: "A5D6A7" } };
                if (c === 10) border.right = { style: "thin", color: { rgb: "A5D6A7" } };
                return border;
            };

            const range = XLSX.utils.decode_range(ws['!ref'] || "");
            for (let r = range.s.r; r <= range.e.r; ++r) {
                const isAlternate = r % 2 === 0;
                // Soft mint green tint for alternating rows
                const rowFill = isAlternate ? { fgColor: { rgb: "F5F8F6" } } : { fgColor: { rgb: "FFFFFF" } };

                for (let c = range.s.c; c <= range.e.c; ++c) {
                    const cellRef = XLSX.utils.encode_cell({ r, c });
                    if (!ws[cellRef]) continue;

                    if (r === 0) {
                        ws[cellRef].s = {
                            fill: { fgColor: { rgb: "FFFFFF" } }
                        };
                    } else if (r === 1) {
                        if (c <= 8) {
                            ws[cellRef].s = titleStyle;
                        } else {
                            ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                        }
                    } else if (r === 2) {
                        if (c <= 8) {
                            ws[cellRef].s = subtitleStyle;
                        } else {
                            ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                        }
                    } else if (r === 3) {
                        if (c <= 8) {
                            ws[cellRef].s = metaStyle;
                        } else {
                            ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                        }
                    } else if (r === 4) {
                        // Spacer row between generated metadata and table headers
                        ws[cellRef].s = {
                            fill: { fgColor: { rgb: "FFFFFF" } }
                        };
                    } else if (r === 5) {
                        ws[cellRef].s = headerStyle;
                    } else if (r > 5 && r < 6 + exportData.length) {
                        // Data Rows
                        if (c >= 7) {
                            // Opening, Credit, Debit, Closing columns
                            let textCol = "1F2937";
                            let isBold = false;
                            const val = Number(ws[cellRef].v);

                            if (c === 7) {
                                // Opening
                                if (val > 0) {
                                    textCol = "047857";
                                    isBold = true;
                                } else {
                                    textCol = "6B7280"; // Muted color for 0
                                }
                            } else if (c === 8) {
                                // Credit
                                if (val > 0) {
                                    textCol = "047857";
                                    isBold = true;
                                } else {
                                    textCol = "6B7280";
                                }
                            } else if (c === 9) {
                                // Debit
                                if (val > 0) {
                                    textCol = "DC2626"; // Red font for debited/dispensed items
                                    isBold = true;
                                } else {
                                    textCol = "6B7280";
                                }
                            } else if (c === 10) {
                                // Closing
                                if (val > 0) {
                                    textCol = "047857";
                                    isBold = true;
                                } else {
                                    textCol = "6B7280";
                                }
                            }

                            ws[cellRef].s = {
                                font: { name: "Segoe UI", sz: 10, bold: isBold, color: { rgb: textCol } },
                                fill: rowFill,
                                alignment: { vertical: "center", horizontal: "center" },
                                border: cellBorder
                            };

                            // Explicit integer formatting
                            ws[cellRef].z = "0";
                            ws[cellRef].t = "n";
                        } else if (c === 5) {
                            // Action Name (Dispense / Return) - color-coded text only
                            const actionVal = ws[cellRef].v?.toString()?.toLowerCase() || '';
                            let actionText = "1D4ED8"; // default blue
                            if (actionVal === 'dispense') {
                                actionText = "047857"; // green
                            } else if (actionVal === 'return') {
                                actionText = "B91C1C"; // red
                            }
                            ws[cellRef].s = {
                                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: actionText } },
                                fill: rowFill,
                                alignment: { vertical: "center", horizontal: "center" },
                                border: cellBorder
                            };
                        } else if (c === 2) {
                            // IP Category - colored text only
                            const ipVal = ws[cellRef].v?.toString() || '';
                            ws[cellRef].s = getIpCategoryStyle(ipVal, rowFill);
                        } else {
                            // Standard Text Columns (A-E, G)
                            ws[cellRef].s = {
                                ...textStyle,
                                fill: rowFill
                            };
                        }
                    } else if (r === 6 + exportData.length) {
                        // Spacer row before footer
                        ws[cellRef].s = {
                            fill: { fgColor: { rgb: "FFFFFF" } }
                        };
                    } else if (r === 6 + exportData.length + 1) {
                        // Footer warning row
                        ws[cellRef].s = { ...footerStyle, border: getFooterBorder(c) };
                    }
                }
            }

            // Aesthetic Row Heights
            ws['!rows'] = [
                { hpt: 12 }, // Row 1: Spacer
                { hpt: 38 }, // Row 2: Title
                { hpt: 22 }, // Row 3: Subtitle
                { hpt: 22 }, // Row 4: Meta info
                { hpt: 12 }, // Row 5: Spacer between generated and table (new!)
                { hpt: 32 }  // Row 6: Column headers
            ];
            for (let r = 6; r < 6 + exportData.length; r++) {
                ws['!rows'].push({ hpt: 24 }); // Generous row height for clean data layout
            }
            ws['!rows'].push({ hpt: 15 }); // Spacer before warning note
            ws['!rows'].push({ hpt: 30 }); // Warning Note row

            // Tailored Column Widths (Reduced to prevent horizontal scrolling)
            ws['!cols'] = [
                { wch: 11 }, // Subject No
                { wch: 13 }, // Study Code
                { wch: 18 }, // IP Category
                { wch: 16 }, // Product Name
                { wch: 13 }, // Batch Lot No
                { wch: 13 }, // Action Name
                { wch: 13 }, // Action Date
                { wch: 11 },  // Opening
                { wch: 11 },  // Credit
                { wch: 11 },  // Debit
                { wch: 11 }   // Closing
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Subject Wise IP");
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid className="px-4">
                <div className="bg-white p-4 rounded shadow-sm mb-4 border border-light">
                    {/* Screen / Summary Cards */}
                    <Row className="mb-4 g-3 px-1">
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL SUBJECTS"
                                value={stats.totalSubjects}
                                subtext="enrolled subjects in study"
                                icon={<i className="ri-user-heart-line" />}
                                bgColor="#f5f3ff"
                                iconColor="#7c3aed"
                                trendType="info"
                                subtextIcon="ri-group-line"
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL DISPENSED"
                                value={stats.totalDispensed}
                                subtext="total units dispensed to subjects"
                                icon={<i className="ri-hand-coin-line" />}
                                bgColor="#fffbeb"
                                iconColor="#d97706"
                                trendType="negative"
                                subtextIcon="ri-arrow-right-up-line"
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <StatCard
                                title="TOTAL RETURNED"
                                value={stats.totalReturn}
                                subtext="units returned by subjects"
                                icon={<i className="ri-refund-2-line" />}
                                bgColor="#ecfdf5"
                                iconColor="#059669"
                                trendType="success"
                                subtextIcon="ri-arrow-left-down-line"
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <StatCard
                                title="NET REMAINING"
                                value={stats.netRemaining}
                                subtext="net stock remaining across study"
                                icon={<i className="ri-database-2-line" />}
                                bgColor="#eff6ff"
                                iconColor="#2563eb"
                                trendType="info"
                                subtextIcon="ri-bubble-chart-line"
                            />
                        </Col>
                    </Row>

                    {/* Table Section Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4 mt-5">
                        <div>
                            <h5 className="mb-1 text-dark fw-bold fs-16">Subject Wise IP Report</h5>
                            <p className="text-muted mb-0 fs-13">Showing {apiData?.[0]?.total_count} records</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className={sf.wrapper}>
                                <CommonSearch onSearch={onSearch} searchText={searchText} />
                                <button
                                    type="button"
                                    className={`${sf.filterBtn} ${isFilterApplied ? sf.active : ""}`}
                                    onClick={toggleFilter}
                                    title="Filter Report"
                                >
                                    <i className="ri-filter-3-line" style={{ fontSize: 15 }} />
                                    {isFilterApplied && <span className={sf.filterDot} />}
                                </button>
                            </div>
                            <Button
                                color="light"
                                className="d-flex align-items-center gap-2 text-dark fs-13 border border-light-subtle px-3 py-2"
                                style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}
                                onClick={handleExport}
                                disabled={isExporting}
                            >
                                {isExporting ? (
                                    <Spinner size="sm" className="me-1" />
                                ) : (
                                    <i className="ri-file-excel-2-line text-success fs-15"></i>
                                )}
                                Export Excel
                            </Button>
                        </div>
                    </div>

                    {/* Common Table */}
                    <div className="border rounded border-light overflow-hidden">
                        <CommonTableIndexWise
                            columns={columns}
                            data={apiData}
                            loading={subjectWiseQuery.isLoading || subjectWiseQuery.isFetching}
                            pageSize={itemsPerPage}
                            currentPage={currentPage}
                            totalRecords={apiData?.[0]?.total_count || 0}
                            onPageChange={setCurrentPage}
                            hideAction={true}
                        />
                    </div>
                </div>
            </Container>

            <SubjectWiseFilterModal
                isOpen={isFilterOpen}
                toggle={toggleFilter}
                onApply={applyFilters}
                onClear={clearFilters}
                existingFilters={filters}
                activeStudyId={activeStudyId}
            />
        </div>
    );
};

export default SubjectWiseReport;
